/* eslint-disable react/prop-types */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { toast } from 'react-toastify';
import Button from '@material-ui/core/Button';
import PostAddIcon from '@material-ui/icons/PostAdd';
import Skeleton from '@material-ui/lab/Skeleton';
import JobTable from '../JobTable';
import EditDeleteButtons from './EditDeleteButtons';
import DefaultColumnFilter from '../../react-table/DefaultColumnFilter';
import AttachmentLink from './AttachmentLink';
import ConfirmationDialog from '../ConfirmationDialog';
import model from '../assets/models/applicantModel';
import deleteJobAttachment from '../functions/deleteJobAttachment';
import updateJob from '../functions/updateJob';

const useStyles = makeStyles((theme) => ({
  button: {
    float: 'right',
    marginBottom: theme.spacing(1),
  },
}));

const Applicants = ({
  applicants,
  id,
  lastUpdate,
  disabled,
  isLoading,
  onApplicantDialogOpen,
  onComplete,
}) => {
  const classes = useStyles();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [applicantToDelete, setApplicantToDelete] = useState({});

  const onDeleteConfirmationClose = useCallback(() => {
    setApplicantToDelete({});
    setDeleteConfirmationOpen(false);
  }, []);

  const selectedApplicantToDelete = useCallback((applicant) => {
    setApplicantToDelete(applicant);
    setDeleteConfirmationOpen(true);
  }, []);

  const confirmedDelete = useCallback(
    async (applicant) => {
      try {
        await deleteJobAttachment(id, applicant.attachment);

        const updatedApplicants = applicants.filter(
          (app) =>
            app.attachment.attachmentId !== applicant.attachment.attachmentId
        );

        const job = {
          id,
          lastUpdate,
          applicants: updatedApplicants,
        };

        await updateJob(job);
        onComplete();
        onDeleteConfirmationClose();
        toast.success('Deleted job applicant successfully.');
      } catch (error) {
        if (
          error.response &&
          error.response.data === 'The conditional request failed'
        ) {
          return toast.error(
            'Failed to delete job applicant, job posting has been modified. Please refresh and try again.'
          );
        }
        toast.error('Failed to delete job applicant.');
      }
      return true;
    },
    [id, lastUpdate, applicants, onComplete, onDeleteConfirmationClose]
  );

  const defaultColumn = {
    Filter: DefaultColumnFilter, // sets up default column filter input type
  };

  // Initial state of table parameters
  const initialState = {
    pageSize: 25,
    sortBy: [
      {
        id: model.name.id,
        desc: false,
      },
    ],
  };

  const columns = React.useMemo(() => {
    const columnList = [
      {
        Header: model.name.name,
        accessor: model.name.id,
      },
      {
        Header: model.company.name,
        accessor: model.company.id,
      },
      {
        Header: model.comment.name,
        accessor: model.comment.id,
      },
      {
        Header: 'Resume',
        accessor: 'attachment.attachmentName',
        id: 'Resume',
        Cell: ({ row: { original } }) => {
          return <AttachmentLink attachment={original.attachment} id={id} />;
        },
      },
    ];
    if (!disabled) {
      columnList.unshift({
        id: 'EditDelete',
        disableFilters: true,
        Cell: ({ row: { original } }) => {
          return (
            <EditDeleteButtons
              row={original}
              selectedJobToDelete={selectedApplicantToDelete}
              onOpen={onApplicantDialogOpen}
            />
          );
        },
      });
    }
    return columnList;
  }, [id, onApplicantDialogOpen, selectedApplicantToDelete, disabled]);

  const tableData = React.useMemo(
    () => (isLoading ? Array(3).fill({}) : applicants),
    [isLoading, applicants]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton />,
          }))
        : columns,
    [isLoading, columns]
  );

  const tableHookProps = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  return (
    <>
      {!disabled && (
        <Button
          className={classes.button}
          onClick={() => onApplicantDialogOpen()}
          color="primary"
          variant="contained"
          startIcon={<PostAddIcon />}
        >
          Add
        </Button>
      )}
      <JobTable
        tableHookProps={tableHookProps}
        tableColumns={tableColumns}
        onOpen={onApplicantDialogOpen}
        columnClicksToIgnore={['EditDelete', 'Resume']}
      />
      {applicantToDelete.name ? (
        <ConfirmationDialog
          onClose={onDeleteConfirmationClose}
          open={deleteConfirmationOpen}
          job={applicantToDelete}
          confirmedAction={confirmedDelete}
          confirmationAction="Delete"
          confirmationText={
            <>
              Please confirm you want to delete the applicant{' '}
              <b>{applicantToDelete.name}</b>
            </>
          }
          confirmationTitle={
            <b>Delete Confirmation ({applicantToDelete.name})</b>
          }
        />
      ) : null}
    </>
  );
};

Applicants.defaultProps = {
  applicants: [],
  isLoading: false,
  onApplicantDialogOpen: () => {},
  onComplete: () => {},
};

Applicants.propTypes = {
  applicants: PropTypes.array,
  id: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  onApplicantDialogOpen: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  lastUpdate: PropTypes.number.isRequired,
  onComplete: PropTypes.func,
};

export default Applicants;
